/**
 * Asambleas — llamadas al backend (functions/asambleas).
 * Todo pasa por callables: el cliente no lee ni escribe las colecciones
 * de asambleas ni de actas directo. Los archivos (audio, minuta, PDF) van
 * por URLs firmadas que emite el servidor, siempre con los headers que
 * vienen en la respuesta (en modo local el emulador de Storage los pide).
 *
 * Fase 3 (Zoom): sala, iniciarAnfitrion, accesoSala, reintentarSala y
 * pasarAManual (staff), entrar (propietario, con Zoom o con el link manual) y
 * entrarPublico (enlace /z/:t de ingreso, o /asamblea/:t con link manual, sin
 * sesión); incluirSesionZoom, descartarSesionZoom y cancelarMinuta (staff,
 * sección Acta).
 * Ningún link de Zoom se guarda en el cliente: el start_url y el acceso de la
 * sala se piden frescos en el click y solo se usan para abrir la pestaña.
 *
 * Fase 4 (Modo Sala): sala* (ops de asambleaAdmin, staff del edificio) para el
 * check-in, el quórum en vivo, la grabación por partes, sus huecos (salaHueco)
 * y los marcadores, y la ventana del Modo Sala (el día de la asamblea, de T-3 h
 * a T+10 h).
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { idiomaSoportado } from "./idioma";

const llamar = async (nombre, data, opciones) => {
  const fn = opciones ? httpsCallable(functions, nombre, opciones) : httpsCallable(functions, nombre);
  const res = await fn(data);
  return res.data;
};

/**
 * Staff del edificio: listar, detalle, guardar, convocar, reprogramar,
 * cancelar, reenviar, marcarEnMano y, con Zoom, sala, iniciarAnfitrion,
 * accesoSala, reintentarSala y pasarAManual.
 */
export const asambleaAdmin = (op, data = {}) => llamar("asambleaAdmin", { op, ...data });

/** Propietario vigente: listar, rsvp, ics, domicilio, actas, actaPdf y entrar. */
export const asambleaPropietario = (op, data = {}) => llamar("asambleaPropietario", { op, ...data });

// "publicar" avisa a los propietarios dentro de la misma llamada: con el
// timeout por defecto del SDK (70 s) un edificio grande daría deadline-exceeded
// aunque el servidor termine bien.
const TIMEOUT_ACTA_MS = { publicar: 540000 };

/**
 * Staff del edificio: actas (listar, detalle, crear, guardar, eliminar),
 * audio de la asamblea (subirAudio, registrarAudio, quitarAudio — { aid, path }
 * de un archivo subido o { aid, sesionId } de una grabación de Modo Sala —,
 * procesar — con confirmarReproceso y/o confirmarSalaGrabando cuando el
 * servidor lo pide —, cancelarProcesamiento, estadoProcesamiento, audioUrl,
 * incluirSesionZoom, descartarSesionZoom),
 * minuta (subirMinuta, minutaTexto, aplicarMinuta, estadoMinuta, cancelarMinuta, deshacer)
 * y publicación (pdfSubida, publicar, pdfUrl).
 */
export const actaAdmin = (op, data = {}) =>
  llamar("actaAdmin", { op, ...data }, TIMEOUT_ACTA_MS[op] ? { timeout: TIMEOUT_ACTA_MS[op] } : undefined);

/**
 * Staff: cancela un procesamiento que quedó en cola y no va a correr (el
 * worker está apagado para el edificio) o que quedó colgado con el lease
 * vencido. Si ya está corriendo, el servidor responde failed-precondition
 * { motivo: "en_curso" }. No exige el edificio habilitado: cancelar no cuesta nada.
 */
export const cancelarProcesamiento = (aid) => actaAdmin("cancelarProcesamiento", { aid });

/**
 * Staff: lo mismo para una minuta (de audio o escrita) que quedó en cola con el worker
 * apagado (misma regla y mismos errores que cancelarProcesamiento). Sale de la
 * cola sin aplicarse y deja de trabar el acta.
 */
export const cancelarMinuta = (actaId, minutaId) => actaAdmin("cancelarMinuta", { actaId, minutaId });

/**
 * Staff: suma al acta una sesión de Zoom que empezó fuera del horario de la
 * asamblea (después del fin previsto, o mucho después de que terminó la
 * primera). El worker no las importa solas: quedan en
 * procesamiento.zoom.instancias con requiereConfirmacion hasta que el staff
 * las incluye. `uuid` es el de la instancia que devolvió estadoProcesamiento.
 * Deja el procesamiento en cola como `procesar` (mismas reglas): si ya hay un
 * acta, sin `confirmarReproceso` responde { requiereConfirmacion: true }.
 * Respuestas: { estado: "pendiente", incluida, workerHabilitado } | { estado,
 * incluida } (se suma a lo que ya estaba en cola) | { yaIncluida: true }.
 */
export const incluirSesionZoom = (aid, uuid, { confirmarReproceso = false } = {}) =>
  actaAdmin("incluirSesionZoom", { aid, uuid, ...(confirmarReproceso ? { confirmarReproceso: true } : {}) });

/**
 * Staff: deja afuera del acta una sesión de Zoom que espera confirmación (la
 * contracara de incluirSesionZoom, con las mismas reglas: auditada y en la
 * misma transacción). La instancia queda con descartadaEnMs y el worker ya no
 * la espera: una asamblea corta que quedó trabada en "zoom_sesion_por_confirmar"
 * se arma con las otras sesiones. Solo manda { aid, uuid }; `confirmarReproceso`
 * viaja únicamente si el servidor lo pidió antes ({ requiereConfirmacion: true }).
 * Respuestas: { estado: "pendiente", descartada, workerHabilitado } | { estado,
 * descartada } | { yaDescartada: true } | { requiereConfirmacion: true }.
 */
export const descartarSesionZoom = (aid, uuid, { confirmarReproceso = false } = {}) =>
  actaAdmin("descartarSesionZoom", { aid, uuid, ...(confirmarReproceso ? { confirmarReproceso: true } : {}) });

/** ¿procesar rechazó volver a procesar porque el worker no está habilitado para el edificio? */
export const esWorkerApagado = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "worker_apagado";

/** ¿cancelarProcesamiento llegó tarde: el worker ya lo está procesando? */
export const esProcesamientoEnCurso = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "en_curso";

/**
 * ¿procesar se rechazó porque una grabación de Modo Sala sigue grabando (o
 * subiendo lo último) y todavía no hay ningún audio? Con audios, en cambio,
 * procesar responde { requiereConfirmacion: true, motivo: "sala_grabando",
 * sesiones, grabando, dispositivos } y se confirma con confirmarSalaGrabando: true.
 */
export const esSalaGrabando = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "sala_grabando";

/**
 * ¿procesar (o incluir / descartar una sesión) chocó con la sala de Zoom
 * todavía abierta? La grabación se importa sola al cierre de la sala (la
 * encola el cron); los detalles traen `cierreMs`.
 */
export const esSalaZoomAbierta = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "sala_abierta";

/** Endpoint público del enlace personal (/asamblea/:token). */
export const ASAMBLEA_PUBLICO_URL =
  process.env.REACT_APP_ASAMBLEA_PUBLICO_URL ||
  "https://us-central1-propit-a5831.cloudfunctions.net/asambleaPublico";

/** Mensaje legible de un error de callable. */
export const mensajeDeError = (e) => {
  const msg = e && e.message ? String(e.message) : "";
  return msg.replace(/^(FirebaseError:\s*)/, "") || "No se pudo completar la acción.";
};

/** Código de un error de callable sin el prefijo del SDK ("functions/not-found" → "not-found"). */
export const codigoDeError = (e) => String((e && e.code) || "").replace(/^functions\//, "");

/** Detalles estructurados que mandó el servidor con el error ({motivo, ...}) o {}. */
export const detallesDeError = (e) => (e && e.details && typeof e.details === "object" ? e.details : {});

/** ¿El guardado chocó con otra edición? (el servidor exige la versión actual del acta) */
export const esConflictoDeVersion = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "version";

/**
 * ¿publicar rechazó el número impreso en el PDF? Pasa si otra acta del
 * edificio se publicó en el medio: los detalles traen el `actaNumero` que
 * corresponde ahora, para regenerar el PDF con ese.
 */
export const esNumeroDesactualizado = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "numero";

// El navegador no deja setear estos headers en un XHR ni en un fetch (los arma
// él); si el servidor los devolviera, intentar ponerlos corta la subida.
const HEADERS_PROHIBIDOS = new Set([
  "content-length",
  "host",
  "connection",
  "origin",
  "referer",
  "accept-encoding",
  "cookie",
  "date",
  "expect",
  "keep-alive",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
  "via",
]);

/** Headers que devolvió el servidor, sin los que el navegador no deja setear. */
export function headersEnviables(headers) {
  const out = {};
  Object.entries(headers && typeof headers === "object" ? headers : {}).forEach(([k, v]) => {
    const clave = String(k).toLowerCase();
    if (HEADERS_PROHIBIDOS.has(clave) || clave.startsWith("sec-") || clave.startsWith("proxy-")) return;
    if (v == null) return;
    out[k] = String(v);
  });
  return out;
}

/**
 * PUT de un archivo a una URL firmada V4 de Cloud Storage, con progreso.
 * Usa XMLHttpRequest porque fetch no informa el progreso de subida.
 * Los mensajes de error nunca incluyen la URL (lleva la firma).
 *
 * @param {string} url
 * @param {Blob} cuerpo
 * @param {object} [opciones]
 * @param {string} [opciones.contentType] tiene que coincidir con el que firmó el servidor
 * @param {object} [opciones.headers]     headers extra que devolvió el servidor
 * @param {(pct:number, cargados:number, total:number)=>void} [opciones.onProgreso]
 * @param {AbortSignal} [opciones.signal]
 * @returns {Promise<void>}
 */
export function subirAUrlFirmada(url, cuerpo, { contentType, headers = {}, onProgreso, signal } = {}) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      reject(errorCancelado());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);

    let tieneContentType = false;
    Object.entries(headersEnviables(headers)).forEach(([k, v]) => {
      if (String(k).toLowerCase() === "content-type") tieneContentType = true;
      xhr.setRequestHeader(k, v);
    });
    const tipo = contentType || (cuerpo && cuerpo.type) || "";
    if (!tieneContentType && tipo) xhr.setRequestHeader("Content-Type", tipo);

    if (xhr.upload && onProgreso) {
      xhr.upload.onprogress = (ev) => {
        if (!ev.lengthComputable) return;
        onProgreso(Math.min(100, Math.round((ev.loaded / ev.total) * 100)), ev.loaded, ev.total);
      };
    }
    const alCancelar = () => xhr.abort();
    const limpiar = () => signal && signal.removeEventListener("abort", alCancelar);
    if (signal) signal.addEventListener("abort", alCancelar);

    xhr.onload = () => {
      limpiar();
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgreso) onProgreso(100, cuerpo ? cuerpo.size : 0, cuerpo ? cuerpo.size : 0);
        resolve();
      } else {
        reject(new Error(`No se pudo subir el archivo (error ${xhr.status}).`));
      }
    };
    xhr.onerror = () => {
      limpiar();
      reject(new Error("Se cortó la conexión mientras se subía el archivo. Probá de nuevo."));
    };
    xhr.ontimeout = xhr.onerror;
    xhr.onabort = () => {
      limpiar();
      reject(errorCancelado());
    };
    xhr.send(cuerpo);
  });
}

function errorCancelado() {
  const e = new Error("Subida cancelada.");
  e.name = "AbortError";
  return e;
}

/** Descarga un .ics generado en el servidor. */
export const descargarIcs = (contenido, nombre = "asamblea.ics") => {
  const blob = new Blob([contenido], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Un blob: armado para una descarga con headers vive esto (la pestaña que lo
// abrió ya lo cargó; el reproductor de audio lo reusa mientras tanto).
const VIDA_BLOB_MS = 10 * 60 * 1000;

/**
 * URL lista para abrir o reproducir un archivo que devolvió el servidor
 * ({ url, headers }). Sin headers (producción: URL firmada V4) es la misma URL.
 * Con headers (modo local: el emulador de Storage pide `Authorization: Bearer
 * owner`), una navegación o un <audio> no los mandarían: se baja con fetch y
 * se devuelve un blob: que se revoca solo a los 10 minutos. Los errores no
 * incluyen la URL (lleva la firma).
 *
 * @param {{url: string, headers?: object}} res
 * @returns {Promise<string>}
 */
export async function urlDescargable(res) {
  const url = res && typeof res.url === "string" ? res.url : "";
  if (!url) throw new Error("El servidor no devolvió el archivo.");
  const headers = headersEnviables(res.headers);
  if (!Object.keys(headers).length) return url;
  let resp;
  try {
    resp = await fetch(url, { headers, cache: "no-store" });
  } catch (_) {
    throw new Error("Se cortó la conexión mientras se bajaba el archivo. Probá de nuevo.");
  }
  if (!resp.ok) throw new Error(`No se pudo bajar el archivo (error ${resp.status}).`);
  const blobUrl = URL.createObjectURL(await resp.blob());
  setTimeout(() => URL.revokeObjectURL(blobUrl), VIDA_BLOB_MS);
  return blobUrl;
}

// ───────────────────────── Zoom (Fase 3) ─────────────────────────

/**
 * Staff: estado de la sala de Zoom de una asamblea, sin links:
 * { video: {proveedor, estado, error}, vivo: {estado, grabacion, participantes,
 *   anfitrionConectado, anfitrionSimultaneas, salaConectada, ...} | null, registrants: {activos, cancelados},
 *   licencia, ventanaAnfitrion, capacidad,
 *   sala: {creada, intentos, ultimoError, finalizadaEnMs, borradaEnMs, reprogramacion, accesos} }.
 * sala.reprogramacion / sala.accesos (o null): lo que le falla a la sala ya
 * creada, {codigo, rechazada, intentos, enMs, proximoIntentoMs}. Con la sala
 * creada, un video en "error" se puede iniciar igual.
 */
export const sala = (aid) => asambleaAdmin("sala", { aid });

/**
 * Staff, de T-60 al cierre de la sala: { startUrl } FRESCO para abrir la sala
 * como anfitrión. El servidor no lo guarda y el cliente tampoco: se usa una
 * sola vez para navegar la pestaña que se abrió en el click (Zoom vence el
 * start_url a las pocas horas). El acceso de la computadora del salón no sale
 * de acá: es accesoSala.
 */
export const iniciarAnfitrion = (aid) => asambleaAdmin("iniciarAnfitrion", { aid });

/**
 * Staff, en una mixta: { joinUrl } de la computadora de la sala presencial
 * ("Sala · Edificio": entra como "Sala" y no cuenta para el quórum). Op propia,
 * con su auditoría: no llama a Zoom ni trae el start_url del anfitrión, así
 * el acceso de anfitrión no viaja a la notebook del salón. Se pide en el click
 * y no se guarda, igual que el del anfitrión.
 */
export const accesoSala = (aid) => asambleaAdmin("accesoSala", { aid });

/**
 * Staff, después de un error de la sala. Sin sala (no se pudo crear o ya no
 * existía en Zoom), la vuelve a crear: gasta el cupo diario del edificio
 * (failed-precondition { motivo: "limite_zoom_edificio" } si ya no queda) →
 * { ok, creada, video } | { ok, creada: false, enCurso: true, video }. Con la
 * sala creada, destraba lo que Zoom había rechazado (el cron vuelve a probar el
 * cambio de horario), mira la licencia y los accesos → { ok, video, licencia }.
 */
export const reintentarSala = (aid) => asambleaAdmin("reintentarSala", { aid });

/** Staff: deja Zoom y pasa la asamblea a un link de videollamada pegado a mano (https). */
export const pasarAManual = (aid, linkManual) => asambleaAdmin("pasarAManual", { aid, linkManual });

/**
 * Propietario con citación, de T-15 al cierre de la sala: { joinUrl, webUrl,
 * proveedor }. Con Zoom, su acceso personal (webUrl = versión navegador); con
 * link manual, el link que pegó el staff (webUrl null: es el mismo para todos).
 */
export const entrar = (aid) => asambleaPropietario("entrar", { aid });

const PROVEEDORES_VIDEO = new Set(["zoom", "manual"]);

/**
 * Enlace sin sesión: POST { op: "entrar", t } al endpoint público (el GET no
 * tiene efectos). Lo usan /z/:t (enlace de ingreso de 15 minutos antes) y
 * /asamblea/:t con link manual (la citación entra desde T-15; con Zoom, la
 * citación no da el acceso personal). Nunca tira; devuelve:
 *   { joinUrl, webUrl, proveedor?, asamblea? }   ya se puede entrar
 *   { todavia: true, habilitaEnMs }              todavía no (se habilita a T-15)
 *   { error: "vencido" | "no_encontrado" | "terminada" | "cancelada"
 *            | "no_disponible" | "sin_video" | "sin_acceso" | "red" | "error" }
 * "no_disponible": el módulo está apagado (el rollback) y no sale ningún
 * acceso; no se arregla reintentando.
 * Si el servidor manda el idioma del propietario (`idioma`: es | en | pt-BR),
 * cualquiera de esas respuestas lo trae: la página se muestra en ese idioma.
 * El token no se loguea ni viaja en el Referer.
 */
export async function entrarPublico(token) {
  if (!token || typeof token !== "string") return { error: "no_encontrado" };
  let resp;
  try {
    resp = await fetch(ASAMBLEA_PUBLICO_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ op: "entrar", t: token }),
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
  } catch (_) {
    return { error: "red" };
  }
  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    body = null;
  }
  const idioma = idiomaSoportado(body && body.idioma);
  const conIdioma = (out) => (idioma ? { ...out, idioma } : out);
  const codigo = body && typeof body.error === "string" ? body.error : "";
  if (resp.status === 410 || codigo === "vencido") return conIdioma({ error: "vencido" });
  if (resp.status === 404 || codigo === "no_encontrado") return conIdioma({ error: "no_encontrado" });
  if (codigo === "cancelada") return conIdioma({ error: "cancelada" });
  // Módulo apagado (asambleas_config/global.enabled false): 409 sin acceso.
  if (codigo === "no_disponible") return conIdioma({ error: "no_disponible" });
  if (["terminada", "finalizada", "cerrada"].includes(codigo)) return conIdioma({ error: "terminada" });
  // Link manual sin pegar todavía / acceso personal de Zoom todavía sin crear.
  if (codigo === "sin_video" || codigo === "sin_acceso") return conIdioma({ error: codigo });
  // "Todavía no" puede venir con 200 o como error: en los dos casos trae la hora.
  if (body && (body.todavia === true || codigo === "todavia")) {
    const ms = Number(body.habilitaEnMs);
    return conIdioma({ todavia: true, habilitaEnMs: Number.isFinite(ms) && ms > 0 ? ms : null });
  }
  if (!resp.ok || !body) return conIdioma({ error: "error" });
  const joinUrl = urlNavegable(body.joinUrl);
  if (!joinUrl) return conIdioma({ error: "error" });
  const out = { joinUrl, webUrl: urlNavegable(body.webUrl) };
  if (PROVEEDORES_VIDEO.has(body.proveedor)) out.proveedor = body.proveedor;
  if (body.asamblea && typeof body.asamblea === "object") out.asamblea = body.asamblea;
  return conIdioma(out);
}

const HOSTS_LOCALES = new Set(["127.0.0.1", "localhost"]);

/**
 * El link sirve para navegar: https siempre; http solo a 127.0.0.1/localhost
 * en el front local (el simulador de Zoom de scripts/local). Cualquier otra
 * cosa (javascript:, data:, vacío) → null. Devuelve el string original.
 */
export function urlNavegable(u) {
  if (typeof u !== "string" || !u.trim()) return null;
  let url;
  try {
    url = new URL(u.trim());
  } catch (_) {
    return null;
  }
  if (url.protocol === "https:") return u.trim();
  if (url.protocol === "http:" && HOSTS_LOCALES.has(url.hostname) && process.env.REACT_APP_LOCAL === "true") return u.trim();
  return null;
}

// ── Ventanas de tiempo (espejo de finMs, cierreSalaMs, ventanaEntrar y ──────
// ── ventanaAnfitrion de functions/asambleas/lib/zoomLib.js) ─────────────────
// Deciden el servidor y su reloj: el cliente las usa solo para mostrar u
// ocultar los botones a tiempo (api.test.js compara las dos con los mismos casos).

const MIN_MS = 60 * 1000;
const TZ = "America/Montevideo";

/**
 * Minutos alrededor de la asamblea (MARGENES de zoomLib): el propietario entra
 * desde T-15, el anfitrión inicia desde T-60 y la sala vive hasta fin + 60 (o
 * hasta fin + la espera de la 2ª convocatoria, si es mayor).
 */
export const MINUTOS_ZOOM = Object.freeze({ entrarAntes: 15, anfitrionAntes: 60, despues: 60 });

const msValido = (v) => typeof v === "number" && Number.isFinite(v);

/** Fin previsto: inicio + duración estimada (120 si falta), como zoomLib.finMs. */
export function finAsambleaMs(a) {
  const inicio = Number(a && a.inicioMs);
  if (!Number.isFinite(inicio)) return null;
  const duracion = Number(a.duracionMin) > 0 ? Number(a.duracionMin) : 120;
  return inicio + duracion * MIN_MS;
}

/**
 * Cierre de la sala (zoomLib.cierreSalaMs): fin + 60 min, o fin + la espera
 * de la 2ª convocatoria si es mayor (una asamblea que arranca en segunda
 * convocatoria termina más tarde). null si la asamblea no tiene fecha.
 */
export function cierreSalaMs(a) {
  const fin = finAsambleaMs(a);
  if (fin == null) return null;
  return fin + Math.max(MINUTOS_ZOOM.despues, Number(a.minutosSegunda) || 0) * MIN_MS;
}

/**
 * "Entrar" del propietario: de T-15 al cierre de la sala (así puede volver a
 * entrar si se le corta o si la asamblea se alarga). null sin fecha.
 */
export function ventanaEntrar(a) {
  const hasta = cierreSalaMs(a);
  if (hasta == null) return null;
  return { desdeMs: Number(a.inicioMs) - MINUTOS_ZOOM.entrarAntes * MIN_MS, hastaMs: hasta };
}

/**
 * "Iniciar como anfitrión": de T-60 al cierre de la sala. Si el servidor ya
 * mandó la suya (`sala.ventanaAnfitrion`, con desdeMs y hastaMs), se usa esa.
 */
export function ventanaAnfitrion(a, delServidor = null) {
  if (delServidor && msValido(delServidor.desdeMs) && msValido(delServidor.hastaMs)) {
    return { desdeMs: delServidor.desdeMs, hastaMs: delServidor.hastaMs };
  }
  const hasta = cierreSalaMs(a);
  if (hasta == null) return null;
  return { desdeMs: Number(a.inicioMs) - MINUTOS_ZOOM.anfitrionAntes * MIN_MS, hastaMs: hasta };
}

/** ¿El instante cae en la ventana [desde, hasta)? */
export const enVentana = (v, ahoraMs) => !!v && ahoraMs >= v.desdeMs && ahoraMs < v.hastaMs;

/** Milisegundos hasta el próximo límite posterior a `ahoraMs`, o null si no queda ninguno. */
export function msHastaProximo(limites, ahoraMs) {
  let min = null;
  for (const l of limites || []) {
    const n = Number(l);
    if (!Number.isFinite(n) || n <= ahoraMs) continue;
    if (min == null || n - ahoraMs < min) min = n - ahoraMs;
  }
  return min;
}

/** "18:45" en hora de Montevideo. */
export function horaMontevideo(ms) {
  return new Intl.DateTimeFormat("es-UY", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(Number(ms)));
}

/** "05/11" (día y mes en Montevideo; armado a mano: el ICU de es-UY no rellena el día). */
export function diaMesMontevideo(ms) {
  const [, mes, dia] = fechaMontevideo(ms).split("-");
  return `${dia}/${mes}`;
}

/** "YYYY-MM-DD" (fecha calendario de Montevideo) de un instante. */
export function fechaMontevideo(ms) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(Number(ms)));
}

/** Diferencia (ms) entre la hora de Montevideo y UTC en ese instante (Uruguay hoy: −3 h). */
function offsetMontevideoMs(ms) {
  const partes = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(new Date(ms))
    .forEach((p) => {
      partes[p.type] = p.value;
    });
  const comoUtc = Date.UTC(Number(partes.year), Number(partes.month) - 1, Number(partes.day), Number(partes.hour) % 24, Number(partes.minute), Number(partes.second));
  return comoUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * Instante en que empieza el día anterior a la asamblea (00:00 de D-1 en
 * Montevideo): desde ahí la tarjeta muestra "Instalá Zoom y probá el audio".
 */
export function inicioDiaAnteriorMs(a) {
  const inicio = Number(a && a.inicioMs);
  if (!Number.isFinite(inicio)) return null;
  const [y, m, d] = fechaMontevideo(inicio - 24 * 60 * MIN_MS).split("-").map(Number);
  const medianocheUtc = Date.UTC(y, m - 1, d);
  return medianocheUtc - offsetMontevideoMs(medianocheUtc);
}

// ───────────────────────── Modo Sala (Fase 4) ─────────────────────────
// Ops de asambleaAdmin para el staff del edificio (user_access), solo en la
// ventana del Modo Sala y con la asamblea convocada o en curso. Cada wrapper
// manda exactamente los campos del callable. En una mixta la fuente de audio es
// Zoom: salaIniciarSesion responde failed-precondition { motivo: "mixta_usa_zoom" }
// y el Modo Sala solo hace check-in y marcadores.

/**
 * { sesionId, ext }: abre una sesión de grabación (un stream continuo). Si otra
 * persona del equipo está grabando la asamblea desde otro dispositivo, el
 * servidor responde failed-precondition { motivo: "otro_grabando", dispositivo }
 * (esOtroGrabando): solo con `forzar` (el staff confirmó que graba igual) abre
 * una segunda grabación simultánea. `continuaDe`: "Seguir grabando" después de
 * un corte, la sesión que este dispositivo está terminando de subir (el
 * servidor no la confunde con una recarga ni la cierra). `dispositivoId`: el id
 * de este navegador (grabadora.idDispositivo): el servidor distingue una
 * recarga de otro equipo con la misma cuenta y el mismo navegador. Cada uno
 * viaja solo si se usa.
 */
export const salaIniciarSesion = ({ aid, mimeType, forzar = false, continuaDe = null, dispositivoId = null }) =>
  asambleaAdmin("salaIniciarSesion", {
    aid,
    mimeType,
    ...(forzar === true ? { forzar: true } : {}),
    ...(typeof continuaDe === "string" && continuaDe ? { continuaDe } : {}),
    ...(typeof dispositivoId === "string" && dispositivoId ? { dispositivoId } : {}),
  });

/** { uploadUrl, path, headers }: PUT de la parte n (~60 s) de la sesión. */
export const salaUrlChunk = ({ aid, sesionId, n, bytes }) => asambleaAdmin("salaUrlChunk", { aid, sesionId, n, bytes });

/** { confirmados }: el servidor mira que la parte n exista y pese más de 0 (idempotente). */
export const salaConfirmarChunk = ({ aid, sesionId, n }) => asambleaAdmin("salaConfirmarChunk", { aid, sesionId, n });

/**
 * { ok }: cada 15 s mientras la sesión está abierta, con el nivel del micrófono
 * si se sabe. `grabando` (boolean): si la grabadora está capturando. `fase`:
 * la de la grabadora — "grabando", "interrumpida" (la captura se cortó sola:
 * el servidor guarda desde cuándo y el cron avisa si nadie sigue grabando) o
 * "deteniendo" | "subiendo" | "finalizando" (paró a pedido y sube lo último:
 * no es un corte). El servidor usa `fase` y, si no llega, `grabando`.
 */
export const salaLatido = ({ aid, sesionId, nivelDb, grabando, fase }) =>
  asambleaAdmin("salaLatido", {
    aid,
    sesionId,
    ...(Number.isFinite(nivelDb) ? { nivelDb } : {}),
    ...(typeof grabando === "boolean" ? { grabando } : {}),
    ...(typeof fase === "string" && fase ? { fase } : {}),
  });

/**
 * { ok }: un hueco de la grabación (la captura estuvo en pausa más de 5 s:
 * pantalla bloqueada, otra app, una llamada). desdeMs y hastaMs cuentan desde
 * el inicio de la sesión, como el tMs de los marcadores; el worker lo marca en
 * el acta ("faltó audio") y lo descuenta al ubicar los marcadores. causa:
 * "silenciado" (el sistema silenció la pista) | "oculta" (la página estuvo
 * suspendida). Reenviar el mismo hueco (mismo desdeMs) no lo duplica.
 */
export const salaHueco = ({ aid, sesionId, desdeMs, hastaMs, causa }) =>
  asambleaAdmin("salaHueco", {
    aid,
    sesionId,
    desdeMs: Math.max(0, Math.round(Number(desdeMs) || 0)),
    hastaMs: Math.max(0, Math.round(Number(hastaMs) || 0)),
    ...(typeof causa === "string" && causa ? { causa } : {}),
  });

/**
 * { id }: marcador de "Siguiente punto" (tipo "inicio_punto") o del resultado
 * anotado (tipo "resultado", con resultado aprobado | rechazado | sin_votacion |
 * postergado). tMs: milisegundos desde el inicio de la sesión de grabación (en
 * una mixta, sin sesión, desde que se tocó "Comenzar la asamblea"). atrasoMs:
 * cuánto hace que se tocó (un marcador que esperó en la cola durante un corte
 * de red): el servidor lo ubica en ahora − atrasoMs, no en la hora de llegada.
 */
export const salaMarcador = ({ aid, sesionId = null, puntoId, tipo, resultado = null, tMs, atrasoMs }) =>
  asambleaAdmin("salaMarcador", {
    aid,
    sesionId: sesionId || null,
    puntoId,
    tipo,
    ...(tipo === "resultado" ? { resultado } : {}),
    tMs: Math.max(0, Math.round(Number(tMs) || 0)),
    ...(atrasoMs != null && Number.isFinite(Number(atrasoMs)) ? { atrasoMs: Math.max(0, Math.round(Number(atrasoMs))) } : {}),
  });

/**
 * { faltantes: [n...] } | { ok, audio }: cierra la sesión si están todas las
 * partes 0..totalChunks−1 (y la suma al audio del acta).
 */
export const salaFinalizarSesion = ({ aid, sesionId, totalChunks }) =>
  asambleaAdmin("salaFinalizarSesion", { aid, sesionId, totalChunks });

/** { uploadUrl, path, headers }: PUT de la foto del poder (image/jpeg | image/png, hasta 8 MB). */
export const salaUrlPoder = ({ aid, unidadId, contentType }) => asambleaAdmin("salaUrlPoder", { aid, unidadId, contentType });

/**
 * { checkin }: presente | representada | ausente de una unidad del padrón.
 * Representada exige representante.nombre; poderPath (opcional) es el de la
 * foto que se subió para esa unidad.
 */
export function salaCheckin({ aid, unidadId, estado, representante = null, poderPath = null }) {
  const datos = { aid, unidadId, estado };
  if (estado === "representada") {
    datos.representante = {
      nombre: String((representante && representante.nombre) || "").trim(),
      documento: String((representante && representante.documento) || "").trim() || null,
    };
    if (poderPath) datos.poderPath = poderPath;
  }
  return asambleaAdmin("salaCheckin", datos);
}

/**
 * { sesiones, checkins, marcadores, quorum: { cabezas: {presentes, total,
 * remotas?}, valor: {presente, total}, alcanzado, umbral, soloSala? } }: el
 * quórum lo calcula el servidor sobre el padrón vivo y los check-ins. En una
 * mixta suma a los conectados por Zoom (cabezas.remotas, incluidas en
 * presentes); con link manual no los ve y manda soloSala: true.
 */
export const salaEstado = ({ aid }) => asambleaAdmin("salaEstado", { aid });

/** ¿salaIniciarSesion rechazó grabar porque la asamblea es mixta (el audio sale de Zoom)? */
export const esMixtaUsaZoom = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "mixta_usa_zoom";

/**
 * ¿salaIniciarSesion rechazó grabar porque otra persona del equipo ya graba
 * desde otro dispositivo? Los detalles traen `dispositivo` ("iPhone · Safari
 * 18"); se puede grabar igual con `forzar` (el acta tendría el audio repetido).
 */
export const esOtroGrabando = (e) =>
  codigoDeError(e) === "failed-precondition" && detallesDeError(e).motivo === "otro_grabando";

const HORA_MS = 60 * MIN_MS;

/** Horas alrededor del inicio en que el servidor acepta el Modo Sala. */
export const HORAS_MODO_SALA = Object.freeze({ antes: 3, despues: 10 });

/** Ventana del Modo Sala: de 3 h antes del inicio a 10 h después. null sin fecha. */
export function ventanaModoSala(a) {
  const inicio = Number(a && a.inicioMs);
  if (!Number.isFinite(inicio)) return null;
  return { desdeMs: inicio - HORAS_MODO_SALA.antes * HORA_MS, hastaMs: inicio + HORAS_MODO_SALA.despues * HORA_MS };
}

const MODALIDADES_MODO_SALA = new Set(["presencial", "mixta"]);
const ESTADOS_MODO_SALA = new Set(["convocada", "en_curso"]);

/**
 * ¿Se ofrece "Abrir Modo Sala"? Solo asambleas presenciales o mixtas,
 * convocadas o en curso, el día de la asamblea (o dentro de la ventana, que
 * puede pasar la medianoche). `habilitado` cuando el servidor ya lo acepta
 * (antes, el botón dice a qué hora se habilita). Decide el servidor: esto es
 * solo para mostrar el botón a tiempo.
 * @returns {{mostrar: boolean, habilitado: boolean, desdeMs: number|null, hastaMs: number|null}}
 */
export function modoSalaDisponible(a, ahoraMs) {
  const v = ventanaModoSala(a);
  const base = { mostrar: false, habilitado: false, desdeMs: v ? v.desdeMs : null, hastaMs: v ? v.hastaMs : null };
  if (!v || !MODALIDADES_MODO_SALA.has(a.modalidad) || !ESTADOS_MODO_SALA.has(a.estado)) return base;
  const habilitado = enVentana(v, ahoraMs);
  const hoy = fechaMontevideo(Number(a.inicioMs)) === fechaMontevideo(ahoraMs);
  return { ...base, mostrar: habilitado || (hoy && ahoraMs < v.hastaMs), habilitado };
}

/** Ruta del Modo Sala (pantalla completa, fuera del panel). */
export const rutaModoSala = (aid) => `/sala/${encodeURIComponent(String(aid))}`;

/**
 * Abre el Modo Sala en la MISMA pestaña (navegación completa): el panel queda
 * cerrado mientras se graba, así su cierre por inactividad no corta la sesión
 * de Firebase en medio de la asamblea.
 */
export function abrirModoSala(aid) {
  window.location.assign(rutaModoSala(aid));
}
